import { useCallback, useEffect, useRef, useState } from "react";
import { ADMIN_SELECTED_TAB_STORAGE_KEY } from "../constants/adminStorageKeys";
import type { AdminTab, ContentTab } from "../types/admin";
import { isAdminTab } from "../utils/adminTabs";

type UseAdminNavigationParams = {
  challengeIdFromRoute?: number;
  contentRouteMode?: boolean;
  leaguesRouteMode?: boolean;
  historyTypeFilterFromRoute?: string;
  isAdmin: boolean;
  isStaff: boolean;
  isTournamentRouteChallenge: boolean;
};

export function useAdminNavigation({
  challengeIdFromRoute,
  contentRouteMode,
  leaguesRouteMode,
  historyTypeFilterFromRoute,
  isAdmin,
  isStaff,
  isTournamentRouteChallenge,
}: UseAdminNavigationParams) {
  const [tab, setTab] = useState<AdminTab>("history");
  const activeTab: AdminTab = leaguesRouteMode ? "leagues" : contentRouteMode ? "content" : tab;
  const [contentTab, setContentTab] = useState<ContentTab>("hero");
  const [adminNavOpen, setAdminNavOpen] = useState(false);
  const [adminNavCollapsed, setAdminNavCollapsed] = useState(false);
  const initialTabResolvedRef = useRef(false);

  const goToAdminHomeContentPage = useCallback(() => {
    window.location.hash = "#/admin/conteudo";
  }, []);

  const goToAdminHistoryPage = useCallback(() => {
    window.location.hash = "#/admin";
  }, []);

  const goToAdminTournamentsPage = useCallback(() => {
    window.location.hash = "#/admin/torneios";
  }, []);

  const goToAdminListPage = useCallback(() => {
    if (isTournamentRouteChallenge) {
      goToAdminTournamentsPage();
      return;
    }
    goToAdminHistoryPage();
  }, [
    goToAdminHistoryPage,
    goToAdminTournamentsPage,
    isTournamentRouteChallenge,
  ]);

  const selectTab = useCallback(
    (nextTab: AdminTab) => {
      if (nextTab === "content" && !contentRouteMode) return;
      if (!isAdmin && nextTab === "content") return;
      if (!isAdmin && nextTab === "clubs") return;
      if (!isAdmin && nextTab === "categoryRequests") return;
      if (!isAdmin && nextTab === "coupons") return;
      setTab(nextTab);
      sessionStorage.setItem(ADMIN_SELECTED_TAB_STORAGE_KEY, nextTab);
    },
    [contentRouteMode, isAdmin],
  );

  const closeAdminNav = useCallback(() => {
    setAdminNavOpen(false);
  }, []);

  const toggleAdminNav = useCallback(() => {
    setAdminNavOpen((prev) => !prev);
  }, []);

  const toggleAdminDesktopNav = useCallback(() => {
    setAdminNavCollapsed((prev) => !prev);
    setAdminNavOpen((prev) => !prev);
  }, []);

  const navigateAdminTab = useCallback(
    (
      nextTab:
        | "users"
        | "history"
        | "clubs"
        | "categoryRequests"
        | "registrations"
        | "payments"
        | "coupons",
    ) => {
      selectTab(nextTab);
      if (leaguesRouteMode || contentRouteMode || historyTypeFilterFromRoute || challengeIdFromRoute) {
        goToAdminHistoryPage();
      }
      setAdminNavOpen(false);
    },
    [
      challengeIdFromRoute,
      contentRouteMode,
      leaguesRouteMode,
      goToAdminHistoryPage,
      historyTypeFilterFromRoute,
      selectTab,
    ],
  );

  const navigateAdminContent = useCallback(() => {
    if (!isAdmin) return;
    if (!contentRouteMode) {
      goToAdminHomeContentPage();
    }
    setAdminNavOpen(false);
  }, [contentRouteMode, goToAdminHomeContentPage, isAdmin]);

  const navigateAdminTournaments = useCallback(() => {
    goToAdminTournamentsPage();
    selectTab("history");
    setAdminNavOpen(false);
  }, [goToAdminTournamentsPage, selectTab]);

  useEffect(() => {
    if (initialTabResolvedRef.current) return;
    if (!isStaff) return;
    initialTabResolvedRef.current = true;
    if (contentRouteMode) {
      selectTab("content");
      return;
    }
    if (challengeIdFromRoute) {
      selectTab("history");
      return;
    }
    if (historyTypeFilterFromRoute) {
      selectTab("history");
      return;
    }
    const rawStored = String(
      sessionStorage.getItem(ADMIN_SELECTED_TAB_STORAGE_KEY) || "",
    );
    const storedTab = isAdminTab(rawStored) ? rawStored : null;
    const allowedStored =
      storedTab &&
      storedTab !== "content" &&
      (isAdmin || storedTab !== "clubs") &&
      (isAdmin || storedTab !== "categoryRequests");
    const nextTab = allowedStored ? storedTab : "history";
    selectTab(nextTab);
  }, [
    challengeIdFromRoute,
    contentRouteMode,
    leaguesRouteMode,
    historyTypeFilterFromRoute,
    isAdmin,
    isStaff,
    selectTab,
  ]);

  useEffect(() => {
    setAdminNavOpen(false);
  }, [activeTab, contentRouteMode, challengeIdFromRoute, leaguesRouteMode]);

  return {
    activeTab,
    adminNavCollapsed,
    adminNavOpen,
    closeAdminNav,
    contentTab,
    goToAdminHistoryPage,
    goToAdminHomeContentPage,
    goToAdminListPage,
    goToAdminTournamentsPage,
    navigateAdminContent,
    navigateAdminTab,
    navigateAdminTournaments,
    selectTab,
    setAdminNavOpen,
    setContentTab,
    toggleAdminDesktopNav,
    toggleAdminNav,
  };
}
